import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, CloudUpload, Trash2, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { fmtDateTimeWeek } from '../lib/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round((b || 0) / 1024)} KB`);

const OrphanList = ({ items, selected, setSelected }) => {
  const { t } = useTranslation();
  return (
    <div className="max-h-72 overflow-y-auto border rounded-md">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-left text-gray-600 sticky top-0">
          <tr>
            <th className="p-2 w-8"><Checkbox checked={items.length > 0 && selected.length === items.length}
              onCheckedChange={(on) => setSelected(on ? items.map((x) => x.id) : [])} data-testid="orphan-select-all" /></th>
            <th className="p-2">{t('fileVisibility.fileCol')}</th>
            <th className="p-2">{t('orphanFiles.wasOn')}</th>
            <th className="p-2">{t('orphanFiles.size')}</th>
            <th className="p-2">{t('fileVisibility.uploadedCol')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((x) => (
            <tr key={x.id} className="border-t" data-testid={`orphan-row-${x.id}`}>
              <td className="p-2"><Checkbox checked={selected.includes(x.id)}
                onCheckedChange={(on) => setSelected((p) => on ? [...p, x.id] : p.filter((i) => i !== x.id))} data-testid={`orphan-select-${x.id}`} /></td>
              <td className="p-2 max-w-[240px] truncate" title={x.original_filename}>{x.original_filename}</td>
              <td className="p-2 text-gray-500">{t(`fileVisibility.${x.entity_type}`)} <span className="font-mono text-[10px]">{String(x.entity_id).slice(0, 8)}</span></td>
              <td className="p-2 text-gray-500">{fmtSize(x.file_size)}</td>
              <td className="p-2 text-gray-500 whitespace-nowrap">{x.uploaded_at ? fmtDateTimeWeek(x.uploaded_at) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DeleteConfirm = ({ open, setOpen, count, onConfirm }) => {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  useEffect(() => { if (open) setTyped(''); }, [open]);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent data-testid="orphan-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('orphanFiles.confirmTitle', { count })}</AlertDialogTitle>
          <AlertDialogDescription>{t('orphanFiles.confirmBody')}</AlertDialogDescription>
        </AlertDialogHeader>
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" data-testid="orphan-delete-type-input" />
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="orphan-delete-cancel">{t('fileVisibility.cancel')}</AlertDialogCancel>
          <AlertDialogAction disabled={typed !== 'DELETE'} onClick={onConfirm} className="bg-red-600 hover:bg-red-700" data-testid="orphan-delete-confirm">
            {t('orphanFiles.deleteForever')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/** Admin: back up, then permanently delete files whose record no longer exists. */
export const OrphanFileCleanupCard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [backup, setBackup] = useState(null);
  const [drive, setDrive] = useState(false);
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/attachments/orphans`);
      setData(r.data);
      setSelected([]);
    } catch { setData(null); }
  }, []);

  useEffect(() => {
    load();
    axios.get(`${API}/drive/status`).then((r) => setDrive(!!r.data?.connected && !r.data?.error)).catch(() => setDrive(false));
  }, [load]);

  const covered = backup && selected.length > 0 && selected.every((id) => backup.file_ids.includes(id));

  const doBackup = async (target) => {
    setBusy(target);
    try {
      if (target === 'drive') {
        const r = await axios.post(`${API}/admin/attachments/orphans/backup`, { file_ids: selected, target });
        setBackup({ id: r.data.backup_id, file_ids: r.data.file_ids, target, link: r.data.web_link });
        toast.success(t('orphanFiles.driveDone'));
      } else {
        const r = await axios.post(`${API}/admin/attachments/orphans/backup`, { file_ids: selected, target }, { responseType: 'blob' });
        const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'orphan_files_backup.zip';
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a');
        a.href = url; a.download = name; a.click();
        window.URL.revokeObjectURL(url);
        setBackup({ id: r.headers['x-backup-id'], file_ids: (r.headers['x-backup-file-ids'] || '').split(',').filter(Boolean), target });
        toast.success(t('orphanFiles.downloadDone'));
      }
    } catch (e) {
      const detail = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : e.response?.data?.detail;
      toast.error(detail || t('orphanFiles.backupFailed'));
    } finally { setBusy(''); }
  };

  const doDelete = async () => {
    setConfirm(false);
    setBusy('delete');
    try {
      const r = await axios.post(`${API}/admin/attachments/orphans/delete`, { backup_id: backup.id, file_ids: selected });
      toast.success(t('orphanFiles.deleted', { count: r.data.deleted }));
      if (r.data.skipped.length) toast.warning(t('orphanFiles.skipped', { count: r.data.skipped.length }));
      setBackup(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('orphanFiles.deleteFailed'));
    } finally { setBusy(''); }
  };

  if (!data) return null;
  return (
    <Card data-testid="orphan-cleanup-card">
      <CardHeader>
        <CardTitle className="text-base">{t('orphanFiles.title')}</CardTitle>
        <CardDescription>{t('orphanFiles.desc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm" data-testid="orphan-summary">{t('orphanFiles.summary', { count: data.count, size: fmtSize(data.total_size) })}</p>
        {data.count > 0 && (
          <>
            <OrphanList items={data.items} selected={selected} setSelected={setSelected} />
            <div className="rounded-md border p-3 space-y-2 bg-slate-50">
              <p className="text-xs font-medium">{t('orphanFiles.step1')}</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={!selected.length || !!busy} onClick={() => doBackup('download')} data-testid="orphan-backup-download">
                  <Download className="h-4 w-4 mr-1" />{busy === 'download' ? t('orphanFiles.working') : t('orphanFiles.downloadZip')}
                </Button>
                <Button size="sm" variant="outline" disabled={!drive || !selected.length || !!busy} onClick={() => doBackup('drive')} data-testid="orphan-backup-drive"
                  title={drive ? '' : t('orphanFiles.driveNotConnected')}>
                  <CloudUpload className="h-4 w-4 mr-1" />{busy === 'drive' ? t('orphanFiles.working') : t('orphanFiles.toDrive')}
                </Button>
              </div>
              {covered && (
                <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid="orphan-backup-status">
                  <ShieldCheck className="h-3.5 w-3.5" />{t('orphanFiles.backupReady', { count: backup.file_ids.length })}
                  {backup.link && <a className="underline ml-1" href={backup.link} target="_blank" rel="noreferrer">{t('orphanFiles.openDrive')}</a>}
                </p>
              )}
              <p className="text-xs font-medium pt-1">{t('orphanFiles.step2')}</p>
              <Button size="sm" variant="destructive" disabled={!covered || !!busy} onClick={() => setConfirm(true)} data-testid="orphan-delete-button">
                <Trash2 className="h-4 w-4 mr-1" />{t('orphanFiles.deleteSelected', { count: selected.length })}
              </Button>
              {!covered && <p className="text-[11px] text-gray-500">{t('orphanFiles.backupFirst')}</p>}
            </div>
          </>
        )}
        <DeleteConfirm open={confirm} setOpen={setConfirm} count={selected.length} onConfirm={doDelete} />
      </CardContent>
    </Card>
  );
};
